import { ReactNode } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import YouTube, { YouTubeProps } from 'react-youtube';

import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { trackEvent } from '~/features/analytics/track-event';
import { ComputerChipIcon, MessageIcon, PlayIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export type TQSectionProps = {
  footerSlot?: ReactNode;
};
export function TQSection({ footerSlot = null }: TQSectionProps) {
  const { t } = useTranslation();
  return (
    <Box
      as='section'
      bgColor='grey.50'
      padding={{
        base: '56px 0px',
        md: '112px 0px',
      }}
    >
      <ContentContainer
        contentMaxWidth='content-max-width-md'
        display='flex'
        flexDirection='column'
        gap={{
          base: '32px',
          md: '88px',
        }}
      >
        <Flex
          flexDirection='column'
          gap={{
            base: '32px',
            md: '56px',
          }}
        >
          <SectionHeader
            color='grey.500'
            iconSlot={<ComputerChipIcon color='grey.500' height='18px' width='18px' />}
            subtitle={t('enterprise.overview.tq.subtitle') ?? ''}
            descriptionSlot={
              <Text color='grey.500' margin='0 auto' maxWidth='744px'>
                {t('enterprise.overview.tq.description')}
              </Text>
            }
            titleSlot={
              <Heading
                as='h2'
                color='grey.700'
                fontWeight='600'
                letterSpacing='-2px'
                lineHeight='1.1'
                fontSize={{
                  base: '2.25rem',
                  md: '3.375rem',
                }}
                whiteSpace={{
                  base: 'normal',
                  md: 'pre-wrap',
                }}
              >
                {t('enterprise.overview.tq.title')}
              </Heading>
            }
          />
          <Box
            bgColor='black'
            maxWidth='1056px'
            overflow='hidden'
            position='relative'
            borderRadius={{
              md: '12px',
            }}
            height={{
              base: '280px',
              md: '540px',
            }}
            marginX={{
              base: '-16px',
              md: 'unset',
            }}
          >
            <Image
              alt=''
              loading='lazy'
              sizes='1056px'
              src='/images/enterprise/tq-simon-allardice.png'
              style={{
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
              fill
            />
            <Flex
              gap='12px'
              left='50%'
              position='absolute'
              transform='translateX(-50%)'
              bottom={{
                base: '20px',
                md: '40px',
              }}
            >
              <WatchTQVideoButton />
              <ButtonLink
                buttonProps={{
                  leftIcon: <MessageIcon height='20px' width='20px' />,
                  variant: 'outline',
                  colorScheme: 'white',
                  flex: {
                    base: 1,
                    md: 'unset',
                  },
                  display: {
                    base: 'none',
                    md: 'flex',
                  },
                  onClick: () =>
                    trackEvent({
                      name: 'Enterprise CTA Clicked',
                      type: 'click',
                      label: 'contact_us_clicked',
                    }),
                }}
                linkProps={{
                  href: '/get-started/enterprise',
                }}
              >
                {t('common.contactUs')}
              </ButtonLink>
            </Flex>
          </Box>
        </Flex>
        <Flex _empty={{ display: 'none' }} flexDirection='column' gap='88px'>
          {footerSlot}
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function WatchTQVideoButton() {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure({
    onOpen: () =>
      trackEvent({
        name: 'Enterprise CTA Clicked',
        type: 'click',
        label: 'watch_video_clicked',
      }),
  });
  const videoHeight = useBreakpointValue(
    {
      base: '240px',
      md: '649px',
    },
    { fallback: 'md' }
  );
  const videoOptions: YouTubeProps['opts'] = {
    height: videoHeight,
    width: '100%',
    host: 'https://www.youtube-nocookie.com',
    playerVars: {
      rel: 0,
      autoplay: 1,
    },
  };

  const title = t('enterprise.overview.tq.videoTitle');
  const videoId = 'nOcOF7AX2C8';

  return (
    <>
      <Button leftIcon={<PlayIcon height='18px' width='18px' />} width='full' onClick={onOpen}>
        {t('enterprise.overview.tq.watchVideo')}
      </Button>
      <Modal isOpen={isOpen} size='6xl' isCentered onClose={onClose}>
        <ModalOverlay backdropFilter='blur(10px) brightness(0.25)' />
        <ModalContent bg='grey.900' color='white'>
          <VisuallyHidden as={ModalHeader}></VisuallyHidden>
          <ModalCloseButton color='white' />
          <ModalBody>
            <YouTube opts={videoOptions} title={title} videoId={videoId} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
